import * as React from 'react';
import * as echarts from 'echarts/core';
import { BarChart, HeatmapChart, PieChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  VisualMapComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';
import type {
  HeroAffinityMatrixCell,
  HeroAffinityResponse,
  ProductLandscapePayload,
  CustomerLandscapePayload,
  ProductHeatTier,
} from './heroAffinityApi';

echarts.use([
  GridComponent,
  TooltipComponent,
  VisualMapComponent,
  BarChart,
  HeatmapChart,
  PieChart,
  LabelLayout,
  UniversalTransition,
  SVGRenderer,
]);

const ChartRenderer: React.FC<{
  option: Record<string, unknown>;
  height?: number;
  matrixMeta?: HeroAffinityResponse['matrix'];
  onChartClick?: (params: { customerId: number; productId: number }) => void;
}> = ({ option, height = 320, matrixMeta, onChartClick }) => {
  const chartRef = React.useRef<HTMLDivElement>(null);
  const chartInstance = React.useRef<echarts.ECharts | null>(null);

  React.useEffect(() => {
    if (!chartRef.current) return undefined;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    chartInstance.current.setOption(option, true);

    const clickHandler = (params: unknown) => {
      const event = params as { componentType?: string; data?: unknown };
      if (!onChartClick || !matrixMeta || event.componentType !== 'series' || !Array.isArray(event.data)) {
        return;
      }
      const [productIndex, customerIndex] = event.data as number[];
      const customer = matrixMeta.customers[customerIndex];
      const product = matrixMeta.products[productIndex];
      if (customer && product) {
        onChartClick({ customerId: customer.customer_id, productId: product.product_id });
      }
    };

    chartInstance.current.off('click');
    if (onChartClick && matrixMeta) {
      chartInstance.current.on('click', clickHandler);
    }

    const resizeHandler = () => chartInstance.current?.resize();
    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('resize', resizeHandler);
      chartInstance.current?.off('click', clickHandler);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [option, onChartClick, matrixMeta]);

  return <div ref={chartRef} style={{ width: '100%', height }} />;
};

const formatCellLabel = (
  cell: HeroAffinityMatrixCell | undefined,
  cellMetric: 'score' | 'score_pct_of_row'
) => {
  if (!cell || !cell.has_pair) return '';
  const v = cell.display_metric;
  if (v == null) return '';
  if (cellMetric === 'score_pct_of_row') return `${v.toFixed(0)}%`;
  if (v >= 1) return v.toFixed(2);
  return v.toFixed(3);
};

export const CustomerProductMatrixChart: React.FC<{
  matrix: HeroAffinityResponse['matrix'];
  onCellClick?: (customerId: number, productId: number) => void;
}> = ({ matrix, onCellClick }) => {
  const option = React.useMemo(() => {
    if (!matrix) return {};

    const cellByKey = new Map<string, HeroAffinityMatrixCell>();
    matrix.cells.forEach((cell) => {
      cellByKey.set(`${cell.product_index}-${cell.customer_index}`, cell);
    });

    const productNames = matrix.products.map((p) => p.product_name);
    const customerNames = matrix.customers.map((c) => c.customer_name);

    const heatmapData = customerNames.flatMap((_, customerIndex) => (
      productNames.map((__, productIndex) => {
        const cell = cellByKey.get(`${productIndex}-${customerIndex}`);
        const value = cell?.has_pair ? Number(cell.score) || 0 : 0;
        return [productIndex, customerIndex, value];
      })
    ));

    const maxScore = heatmapData.reduce((max, [, , v]) => Math.max(max, v), 0.001);

    return {
      tooltip: {
        position: 'top',
        formatter: (params: { data: number[] }) => {
          const [productIndex, customerIndex, score] = params.data;
          const cell = cellByKey.get(`${productIndex}-${customerIndex}`);
          const customer = matrix.customers[customerIndex];
          const product = matrix.products[productIndex];
          if (!customer || !product) return '';
          const lines = [
            `${customer.customer_name} × ${product.product_name}`,
            cell?.has_pair
              ? `Score: ${Number(score).toFixed(4)}`
              : 'No scored pair',
          ];
          if (cell?.has_pair) {
            lines.push(
              `Sales: ${cell.sales_component ?? '—'}`,
              `Inquiry: ${cell.inquiry_component ?? '—'}`,
              `Watchlist: ${cell.watchlist_component ?? '—'}`
            );
          }
          return lines.join('<br/>');
        },
      },
      grid: { left: '12%', right: '6%', top: '4%', bottom: 100, containLabel: true },
      xAxis: {
        type: 'category',
        data: productNames,
        splitArea: { show: true },
        axisLabel: { interval: 0, rotate: 45, fontSize: 10 },
      },
      yAxis: {
        type: 'category',
        data: customerNames,
        splitArea: { show: true },
        axisLabel: { fontSize: 10 },
      },
      visualMap: {
        min: 0,
        max: maxScore,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: 8,
      },
      series: [
        {
          name: 'Affinity score',
          type: 'heatmap',
          data: heatmapData,
          label: {
            show: true,
            fontSize: 9,
            formatter: (params: { data: number[] }) => {
              const [productIndex, customerIndex] = params.data;
              const cell = cellByKey.get(`${productIndex}-${customerIndex}`);
              return formatCellLabel(cell, matrix.cell_metric);
            },
          },
          emphasis: {
            itemStyle: { shadowBlur: 6, shadowColor: 'rgba(0,0,0,0.2)' },
          },
        },
      ],
    };
  }, [matrix]);

  const handleClick = onCellClick
    ? ({ customerId, productId }: { customerId: number; productId: number }) => {
      onCellClick(customerId, productId);
    }
    : undefined;

  const height = Math.max(480, (matrix?.customers.length || 15) * 28 + 120);

  return (
    <ChartRenderer
      option={option}
      height={height}
      matrixMeta={matrix}
      onChartClick={handleClick}
    />
  );
};

export const HeroLeaderboardChart: React.FC<{
  items: HeroAffinityResponse['hero_leaderboard'];
}> = ({ items = [] }) => {
  const option = React.useMemo(() => {
    const names = items.map((i) => i.product_name);
    const counts = items.map((i) => i.customer_count);
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: '4%', right: '8%', top: '4%', bottom: '4%', containLabel: true },
      xAxis: { type: 'value' },
      yAxis: {
        type: 'category',
        data: names.slice().reverse(),
        axisLabel: { fontSize: 11 },
      },
      series: [
        {
          type: 'bar',
          data: counts.slice().reverse(),
          itemStyle: { color: '#1976d2' },
        },
      ],
    };
  }, [items]);

  return <ChartRenderer option={option} height={Math.max(280, items.length * 22 + 60)} />;
};

const TIER_BAR_COLORS: Record<ProductHeatTier, string> = {
  hot: '#d32f2f',
  medium: '#ed6c02',
  low: '#78909c',
};

/** Buyers with affinity (score > 0) — bar colored by hot/medium/low tier. */
export const ProductLandscapeBarChart: React.FC<{
  landscape: ProductLandscapePayload | undefined;
}> = ({ landscape }) => {
  const option = React.useMemo(() => {
    const items = landscape?.chart_products ?? [];
    if (!items.length) return {};
    const names = items.map((i) => i.product_name);
    const counts = items.map((i) => i.buyer_count);
    const colors = items.map((i) => TIER_BAR_COLORS[i.tier] || '#1976d2');
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: unknown) => {
          const p = Array.isArray(params) ? params[0] : params;
          const idx = (p as { dataIndex?: number }).dataIndex ?? 0;
          const row = items[items.length - 1 - idx];
          if (!row) return '';
          return [
            row.product_name,
            `Tier: ${row.tier}`,
            `Buyers: ${row.buyer_count.toLocaleString('en-IN')}`,
            `Avg score: ${row.avg_score.toFixed(3)}`,
          ].join('<br/>');
        },
      },
      grid: { left: '4%', right: '8%', top: '4%', bottom: '4%', containLabel: true },
      xAxis: { type: 'value', name: 'Buyers with affinity' },
      yAxis: {
        type: 'category',
        data: names.slice().reverse(),
        axisLabel: { fontSize: 10 },
      },
      series: [
        {
          type: 'bar',
          data: counts.slice().reverse(),
          itemStyle: {
            color: (params: { dataIndex: number }) => colors[colors.length - 1 - params.dataIndex],
          },
        },
      ],
    };
  }, [landscape]);

  const n = landscape?.chart_products?.length ?? 0;
  return <ChartRenderer option={option} height={Math.max(320, n * 20 + 80)} />;
};

/** Top products × affinity score strength — customer counts per bucket. */
export const ProductScoreBucketHeatmap: React.FC<{
  landscape: ProductLandscapePayload | undefined;
}> = ({ landscape }) => {
  const heatmap = landscape?.score_bucket_heatmap;
  const option = React.useMemo(() => {
    if (!heatmap?.products?.length) return {};

    const productNames = heatmap.products.map((p) => p.product_name);
    const bucketLabels = heatmap.bucket_labels;

    const cellByKey = new Map<string, number>();
    heatmap.cells.forEach((c) => {
      cellByKey.set(`${c.product_index}-${c.bucket_index}`, c.customer_count);
    });

    const heatmapData = productNames.flatMap((_, productIndex) => (
      bucketLabels.map((__, bucketIndex) => {
        const v = cellByKey.get(`${productIndex}-${bucketIndex}`) ?? 0;
        return [bucketIndex, productIndex, v];
      })
    ));

    const maxVal = heatmapData.reduce((max, [, , v]) => Math.max(max, v as number), 1);

    return {
      tooltip: {
        position: 'top',
        formatter: (params: { data: number[] }) => {
          const [bucketIndex, productIndex, count] = params.data;
          const product = heatmap.products[productIndex];
          const bucket = bucketLabels[bucketIndex];
          return `${product?.product_name ?? ''}<br/>Score ${bucket}<br/>${count} buyers`;
        },
      },
      grid: { left: '14%', right: '8%', top: '4%', bottom: 80, containLabel: true },
      xAxis: {
        type: 'category',
        data: bucketLabels,
        splitArea: { show: true },
        name: 'Affinity score band',
        nameLocation: 'middle',
        nameGap: 28,
      },
      yAxis: {
        type: 'category',
        data: productNames,
        splitArea: { show: true },
        axisLabel: { fontSize: 9 },
      },
      visualMap: {
        min: 0,
        max: maxVal,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: 8,
      },
      series: [
        {
          type: 'heatmap',
          data: heatmapData,
          label: {
            show: true,
            fontSize: 9,
            formatter: (params: { data: number[] }) => {
              const v = params.data[2];
              return v > 0 ? String(v) : '';
            },
          },
        },
      ],
    };
  }, [heatmap]);

  const height = Math.max(400, (heatmap?.products?.length || 10) * 22 + 120);
  return <ChartRenderer option={option} height={height} />;
};

export const ProductTierDonutChart: React.FC<{
  summary: ProductLandscapePayload['tier_summary'] | undefined;
}> = ({ summary }) => {
  const option = React.useMemo(() => {
    if (!summary) return {};
    const data = [
      { name: 'Hot', value: summary.hot, itemStyle: { color: TIER_BAR_COLORS.hot } },
      { name: 'Medium', value: summary.medium, itemStyle: { color: TIER_BAR_COLORS.medium } },
      { name: 'Low', value: summary.low, itemStyle: { color: TIER_BAR_COLORS.low } },
    ].filter((d) => d.value > 0);
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} products ({d}%)' },
      series: [
        {
          type: 'pie',
          radius: ['42%', '68%'],
          center: ['50%', '50%'],
          data,
          label: { formatter: '{b}\n{c}' },
        },
      ],
    };
  }, [summary]);

  return <ChartRenderer option={option} height={280} />;
};

/** How many customers have N products in their affinity list (gradient bar). */
export const CustomerBreadthHistogramChart: React.FC<{
  landscape: CustomerLandscapePayload | undefined;
}> = ({ landscape }) => {
  const option = React.useMemo(() => {
    const hist = landscape?.breadth_histogram ?? [];
    if (!hist.length) return {};

    const sorted = hist.slice().sort((a, b) => b.product_count - a.product_count);
    const labels = sorted.map((h) => `${h.product_count} product${h.product_count === 1 ? '' : 's'}`);
    const counts = sorted.map((h) => h.customer_count);
    const maxCount = Math.max(...counts, 1);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: unknown) => {
          const p = Array.isArray(params) ? params[0] : params;
          const idx = (p as { dataIndex?: number }).dataIndex ?? 0;
          const row = sorted[idx];
          if (!row) return '';
          return [
            `${row.product_count} affinity product${row.product_count === 1 ? '' : 's'}`,
            `${row.customer_count.toLocaleString('en-IN')} customers`,
          ].join('<br/>');
        },
      },
      grid: { left: '8%', right: '8%', top: '8%', bottom: 72, containLabel: true },
      xAxis: {
        type: 'category',
        data: labels,
        name: 'Products in affinity list',
        nameLocation: 'middle',
        nameGap: 32,
        axisLabel: { rotate: labels.length > 12 ? 35 : 0, fontSize: 11 },
      },
      yAxis: {
        type: 'value',
        name: 'Customers',
      },
      visualMap: {
        show: true,
        min: 0,
        max: maxCount,
        orient: 'horizontal',
        left: 'center',
        bottom: 8,
        inRange: {
          color: ['#e3f2fd', '#1976d2', '#0d47a1'],
        },
      },
      series: [
        {
          type: 'bar',
          data: counts,
          itemStyle: {
            borderRadius: [4, 4, 0, 0],
          },
        },
      ],
    };
  }, [landscape]);

  return <ChartRenderer option={option} height={340} />;
};

/** Top customers by breadth of affinity list. */
export const CustomerLandscapeBarChart: React.FC<{
  landscape: CustomerLandscapePayload | undefined;
}> = ({ landscape }) => {
  const option = React.useMemo(() => {
    const items = landscape?.chart_customers ?? [];
    if (!items.length) return {};
    const names = items.map((i) => i.customer_name);
    const counts = items.map((i) => i.product_count);
    const colors = items.map((i) => TIER_BAR_COLORS[i.tier] || '#1976d2');
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: unknown) => {
          const p = Array.isArray(params) ? params[0] : params;
          const idx = (p as { dataIndex?: number }).dataIndex ?? 0;
          const row = items[items.length - 1 - idx];
          if (!row) return '';
          return [
            row.customer_name,
            `Tier: ${row.tier}`,
            `Affinity products: ${row.product_count}`,
            `Avg score: ${row.avg_score.toFixed(3)}`,
          ].join('<br/>');
        },
      },
      grid: { left: '4%', right: '8%', top: '4%', bottom: '4%', containLabel: true },
      xAxis: { type: 'value', name: 'Products in list' },
      yAxis: {
        type: 'category',
        data: names.slice().reverse(),
        axisLabel: { fontSize: 10 },
      },
      series: [
        {
          type: 'bar',
          data: counts.slice().reverse(),
          itemStyle: {
            color: (params: { dataIndex: number }) => colors[colors.length - 1 - params.dataIndex],
          },
        },
      ],
    };
  }, [landscape]);

  const n = landscape?.chart_customers?.length ?? 0;
  return <ChartRenderer option={option} height={Math.max(320, n * 20 + 80)} />;
};

export const CustomerScoreBucketHeatmap: React.FC<{
  landscape: CustomerLandscapePayload | undefined;
}> = ({ landscape }) => {
  const heatmap = landscape?.score_bucket_heatmap;
  const option = React.useMemo(() => {
    if (!heatmap?.customers?.length) return {};

    const customerNames = heatmap.customers.map((c) => c.customer_name);
    const bucketLabels = heatmap.bucket_labels;

    const cellByKey = new Map<string, number>();
    heatmap.cells.forEach((c) => {
      cellByKey.set(`${c.customer_index}-${c.bucket_index}`, c.product_count);
    });

    const heatmapData = customerNames.flatMap((_, customerIndex) => (
      bucketLabels.map((__, bucketIndex) => {
        const v = cellByKey.get(`${customerIndex}-${bucketIndex}`) ?? 0;
        return [bucketIndex, customerIndex, v];
      })
    ));

    const maxVal = heatmapData.reduce((max, [, , v]) => Math.max(max, v as number), 1);

    return {
      tooltip: {
        position: 'top',
        formatter: (params: { data: number[] }) => {
          const [bucketIndex, customerIndex, count] = params.data;
          const customer = heatmap.customers[customerIndex];
          const bucket = bucketLabels[bucketIndex];
          return `${customer?.customer_name ?? ''}<br/>Score ${bucket}<br/>${count} products`;
        },
      },
      grid: { left: '14%', right: '8%', top: '4%', bottom: 80, containLabel: true },
      xAxis: {
        type: 'category',
        data: bucketLabels,
        splitArea: { show: true },
        name: 'Affinity score band',
        nameLocation: 'middle',
        nameGap: 28,
      },
      yAxis: {
        type: 'category',
        data: customerNames,
        splitArea: { show: true },
        axisLabel: { fontSize: 9 },
      },
      visualMap: {
        min: 0,
        max: maxVal,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: 8,
      },
      series: [
        {
          type: 'heatmap',
          data: heatmapData,
          label: {
            show: true,
            fontSize: 9,
            formatter: (params: { data: number[] }) => {
              const v = params.data[2];
              return v > 0 ? String(v) : '';
            },
          },
        },
      ],
    };
  }, [heatmap]);

  const height = Math.max(400, (heatmap?.customers?.length || 10) * 22 + 120);
  return <ChartRenderer option={option} height={height} />;
};
